import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { projectsApi, filesApi, type Project, type ProjectCreateRequest } from '../lib/api'
import { useAuth, usePermissions } from '../contexts/IframeAuthProvider'
import { useCreateProject, useDeleteProject } from '../lib/hooks'
import { StatusChip } from '../components/ui/Badge'
import { relativeTime, timeLeft } from '../lib/utils'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { UploadVendorResponsesModal } from '../components/modals/UploadVendorResponsesModal'
import { ProjectSettingsModal } from '../components/modals/ProjectSettingsModal'
import toast from 'react-hot-toast'
import {
  Plus,
  Search,
  FolderOpen,
  Trash2,
  Settings,
  ArrowUpDown,
  ChevronDown
} from 'lucide-react'
import { Tooltip } from '../components/ui/Tooltip'
import { ProjectIcon } from '../components/ui/ProjectIcon'
import { KebabMenu } from '../components/ui/KebabMenu'
import styles from '../components/ui/ProjectsScroller.module.css'

type SortOption = 'created' | 'status' | 'days_left'

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'created', label: 'Date created' },
  { value: 'status', label: 'Status' },
  { value: 'days_left', label: 'Days left' },
]

export const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const permissions = usePermissions()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setShowUploadScope, handleDraggedFilesUpload } = useOutletContext<{ 
    setShowUploadScope: (show: boolean) => void
    handleDraggedFilesUpload: (files: File[]) => void
  }>()
  const [showUploadResponses, setShowUploadResponses] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [isDragOver, setIsDragOver] = useState(false)
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [sortBy, setSortBy] = useState<SortOption>('created')

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fetch projects from API
  const { data: projectsData, isLoading, error, refetch } = useQuery(
    ['projects', { query: debouncedSearchQuery, limit: 100 }], // Increased limit for better client-side filtering
    () => projectsApi.list({ query: debouncedSearchQuery || undefined, limit: 100 }),
    {
      keepPreviousData: true,
    }
  )

  const deleteProject = useDeleteProject()

  // Handle different possible response structures
  const allProjects = Array.isArray(projectsData?.data) 
    ? projectsData.data 
    : Array.isArray((projectsData as any)?.projects)
    ? (projectsData as any).projects
    : Array.isArray(projectsData) 
    ? projectsData 
    : []

  // Client-side filtering for search (fallback in case API search isn't working)
  const filteredProjects = allProjects.filter((project: Project) => {
    if (!searchQuery.trim()) return true

    const query = searchQuery.toLowerCase()
    return (
      project.title.toLowerCase().includes(query) ||
      project.status.toLowerCase().includes(query) ||
      (project.due_date && project.due_date.toLowerCase().includes(query))
    )
  })

  const projects = [...filteredProjects].sort((a: Project, b: Project) => {
    switch (sortBy) {
      case 'status': {
        // Active projects first, then closed; newest first within each group
        const rank = (p: Project) =>
          p.status === 'completed' || p.status === 'closed' ? 1 : 0
        return (
          rank(a) - rank(b) ||
          new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        )
      }
      case 'days_left': {
        // Closest deadline first (overdue at the very top); no due date last
        const due = (p: Project) =>
          p.due_date ? new Date(p.due_date).getTime() : Number.POSITIVE_INFINITY
        return due(a) - due(b)
      }
      case 'created':
      default:
        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    }
  })

  // DS Status chip (ui/Badge StatusChip) — dashboard collapses everything
  // onto in-progress / closed.
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
      case 'closed':
        return <StatusChip variant="done">Closed</StatusChip>
      default:
        return <StatusChip variant="progress">In progress</StatusChip>
    }
  }

  const normalizeStatus = (status: string): 'in_progress' | 'closed' => {
    switch (status) {
      case 'completed':
      case 'closed':
        return 'closed'
      default:
        return 'in_progress' // Default all projects to in_progress
    }
  }

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      // Filter for PDF and DOC files
      const validFiles = files.filter(file => 
        file.type === 'application/pdf' || 
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/msword'
      )
      
      if (validFiles.length > 0) {
        handleDraggedFilesUpload(validFiles)
      } else {
        toast.error('Please upload PDF or Word documents only')
      }
    }
  }

  const handleDeleteProject = async (projectId: string, projectTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete the project "${projectTitle}"? This will permanently delete the project and all its vendor responses, evaluations, and related data. This action cannot be undone.`)) {
      return
    }

    setDeletingProjectId(projectId)
    
    try {
      await deleteProject.mutateAsync(projectId)
      toast.success('Project deleted successfully')
      refetch() // Refresh the projects list
    } catch (error) {
      console.error('Error deleting project:', error)
      toast.error('Failed to delete project')
    } finally {
      setDeletingProjectId(null)
    }
  }

  const handleOpenSettings = (project: Project) => {
    setSelectedProject(project)
    setShowSettingsModal(true)
  }

  const handleUpdateProject = async (data: { title: string; due_date: string }) => {
    if (!selectedProject) return
    
    try {
      await projectsApi.update(selectedProject.id, data)
      toast.success('Project updated successfully')
      refetch() // Refresh project data
      queryClient.invalidateQueries(['projects']) // Update cache
      setShowSettingsModal(false)
    } catch (error) {
      toast.error('Failed to update project')
      throw error
    }
  }

  return (
    <>
      {/* Main Content */}
        <div className="flex-1 flex flex-col h-screen p-6 overflow-hidden">
          {/* Split Content Area */}
          <div className="flex-1 flex flex-col">
            {/* Upload Scope Documents Area - Fixed Height */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4" style={{ minHeight: '280px' }}>
              {/* Title outside the purple container */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Upload Scope Documents
              </h3>
              
              <div className="flex items-center justify-center">
                <div className="w-full">
                  {/* DS Dropzone — full variant (finch hero), ported from sop-studio */}
                  <div
                    role="button"
                    tabIndex={0}
                    className={`flex flex-col items-center justify-center gap-6 text-center cursor-pointer rounded-lg border border-dashed px-10 py-8 min-h-[260px] transition-colors duration-200 ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2 ${
                      isDragOver
                        ? 'bg-accent-100 border-accent-500'
                        : 'bg-accent-50 border-accent-400 hover:bg-accent-100/60'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => setShowUploadScope(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setShowUploadScope(true)
                      }
                    }}
                  >
                    <img
                      src="/finch-reading.svg"
                      alt=""
                      aria-hidden
                      className="h-[102px] w-[92px] select-none pointer-events-none"
                      draggable={false}
                    />

                    <div className="text-lg font-semibold text-gray-800">
                      Drag and Drop Documents here
                    </div>

                    <span className="inline-flex items-center justify-center px-4 py-2 rounded-sm bg-white border border-brand-800 text-brand-800 text-sm font-semibold hover:bg-gray-100 transition-colors duration-200 ease-standard">
                      Browse your documents
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Projects Grid - Remaining Space */}
            <div className="flex-1 flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 p-6 min-w-0 overflow-hidden min-h-0">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Projects</h2>
                  {permissions.canSeeAllBids && (
                    <p className="text-sm text-gray-500">Showing all bids</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Type to search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 w-64 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-ink-500 transition-colors duration-fast ease-standard"
                    />
                  </div>
                  <div className="relative">
                    <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as SortOption)}
                      aria-label="Sort projects"
                      className="pl-9 pr-8 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white appearance-none cursor-pointer focus:outline-none focus:border-ink-500 transition-colors duration-fast ease-standard"
                    >
                      {SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>
              
              <div className="flex-1 flex flex-col overflow-hidden">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <p className="text-red-600 mb-2">Failed to load projects</p>
                      <p className="text-gray-500 text-sm">Please try again later</p>
                    </div>
                  </div>
                ) : projects.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No projects found</p>
                      {searchQuery && (
                        <p className="text-gray-400 text-sm mt-1">Try adjusting your search</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={`${styles.projectsScroller} w-full max-w-full`}>
                    <div className="flex space-x-4 pb-4">
                      {projects.map((project: Project) => (
                        <div
                          key={project.id}
                          className="flex-shrink-0 w-80 bg-white border border-gray-200 rounded-lg p-6 hover:border-ink-500 transition-colors duration-fast ease-standard cursor-pointer relative"
                          onClick={() => navigate(`/projects/${project.id}`)}
                        >
                          {/* Actions menu */}
                          <div className="absolute top-4 right-4">
                            <KebabMenu
                              items={[
                                {
                                  label: 'Settings',
                                  icon: <Settings className="w-4 h-4" />,
                                  onSelect: () => handleOpenSettings(project),
                                },
                                {
                                  label: 'Delete',
                                  icon: <Trash2 className="w-4 h-4" />,
                                  onSelect: () => handleDeleteProject(project.id, project.title),
                                  destructive: true,
                                  disabled: deletingProjectId === project.id,
                                },
                              ]}
                            />
                          </div>

                          {/* Status Badge */}
                          <div className="mb-6">
                            {getStatusBadge(project.status)}
                          </div>

                          {/* Project Icon, Title + Bid ID */}
                          <div className="flex items-start space-x-4 mb-8">
                            <ProjectIcon
                              status={normalizeStatus(project.status)}
                              className="flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0 pr-8">
                              <Tooltip content={project.title} position="top" triggerClassName="block min-w-0">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
                                  {project.title}
                                </h3>
                              </Tooltip>
                              {project.project_number && (
                                <p className="text-xs text-gray-500 truncate">
                                  Bid #{project.project_number}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Meta line — vendors · published · due (SOP relative-time concept) */}
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 tabular-nums whitespace-nowrap">
                            <span>{project.vendor_count || 0} vendors</span>
                            {project.created_at && (
                              <>
                                <span aria-hidden>·</span>
                                <span>Published {relativeTime(project.created_at)}</span>
                              </>
                            )}
                            {project.due_date && (
                              <>
                                <span aria-hidden>·</span>
                                <span className={new Date(project.due_date).getTime() < Date.now() ? 'text-red-600' : ''}>
                                  {timeLeft(project.due_date)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      {/* Upload Vendor Responses Modal */}
      <UploadVendorResponsesModal
        isOpen={showUploadResponses}
        onClose={() => setShowUploadResponses(false)}
        onSubmit={(responses) => {
          console.log('Vendor responses uploaded:', responses)
          setShowUploadResponses(false)
        }}
      />

      {/* Project Settings Modal */}
      {selectedProject && (
        <ProjectSettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          onSave={handleUpdateProject}
          project={selectedProject}
        />
      )}
    </>
  )
}